import { GeoCoordinates } from '@domain/graphql.types';
import { useReverseGeocodingLookup } from '@infrastructure/mapbox';
import { Geocoder } from '@mapbox/search-js-react';
import { ListItemText, MenuItem, MenuList, Paper, Theme, Typography, useTheme } from '@mui/material';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const css = (theme: Theme) => {
  return `
    .Geocoder {
      border: 0;
      box-shadow: none;
    }

    .Geocoder .SearchIcon {
      left: 1em;
    }

    .Geocoder .ActionIcon {
      right: 1em;
    }

    .Geocoder .Input {
      font-size: 1rem;
      border: 1px solid ${theme.palette.divider};
      border-radius: ${theme.borderRadius.pill};
      min-height: 44px;
    }

    .Geocoder .Input:focus {
      border-color: ${theme.palette.action.active};
    }

    .MapboxSearch .Results {
      padding: 8px 0;
    }

    .MapboxSearch .Results .SuggestionText {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .MapboxSearch .Results .Suggestion {
      padding: 8px 24px;
    }

    .ResultsAttribution {
      display: none;
    }
  }
`;
};

export type AddressSearchResponse = {
  coordinates: GeoCoordinates;
  fullAddress: string;
  regionCode: string;
};

// Matches two signed decimals separated by a comma and/or whitespace, e.g. "40.7128, -74.006".
const LAT_LON_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)\s*$/;

const parseLatLon = (raw: string): GeoCoordinates | null => {
  const match = raw.match(LAT_LON_REGEX);
  if (!match) {
    return null;
  }

  const latitude = Number(match[1]);
  const longitude = Number(match[2]);

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  return { latitude, longitude };
};

type Props = {
  onSelect: (address: AddressSearchResponse) => void;
  recentSearches?: AddressSearchResponse[];
  value?: string;
};

const AddressSearch = forwardRef<HTMLElement, Props>(({ onSelect, recentSearches = [], value }: Props, ref) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const {
    palette: { text },
    borderRadius,
    zIndex,
  } = theme;

  const { findNearestLocationFromCoordinates } = useReverseGeocodingLookup();

  const containerRef = useRef<HTMLDivElement>(null);

  const [isFocused, setIsFocused] = useState(false);
  const [inputText, setInputText] = useState(value || '');

  // Keep the local text in sync when the controlled value changes (e.g. after a selection or clear).
  useEffect(() => {
    setInputText(value || '');
  }, [value]);

  // Show recent searches only when the box is focused and empty; Mapbox's own suggestions take over once typing starts.
  const showRecentSearches = isFocused && !inputText.trim() && recentSearches.length > 0;

  const handleSelectRecent = useCallback(
    (address: AddressSearchResponse) => {
      onSelect(address);
      setIsFocused(false);
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      // Only react to a genuine Enter that Mapbox left unhandled (it calls preventDefault when a
      // suggestion is highlighted). isTrusted filters out the synthetic events we dispatch below.
      if (event.key !== 'Enter' || !event.isTrusted || event.defaultPrevented) {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLInputElement)) {
        return;
      }

      const input = target;
      const coordinates = parseLatLon(input.value);
      if (coordinates) {
        // Lat/lon search: reverse geocode to resolve the address/region, then select it directly.
        event.preventDefault();
        const rawCoordinates = `${coordinates.latitude}, ${coordinates.longitude}`;
        try {
          const { fullAddress, regionCode } = await findNearestLocationFromCoordinates({ lat: coordinates.latitude, lng: coordinates.longitude });
          onSelect({ coordinates, fullAddress: fullAddress || rawCoordinates, regionCode });
        } catch {
          // Network/API failure: still select the raw coordinates so Enter works reliably.
          onSelect({ coordinates, fullAddress: rawCoordinates, regionCode: '' });
        }
        input.blur();
        return;
      }

      // Address search: nudge Mapbox to select the first suggestion. Dispatching ArrowDown then Enter
      // drives its own keyboard handler (highlight first, then retrieve), reusing the normal onRetrieve flow.
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
    },
    [findNearestLocationFromCoordinates, onSelect],
  );

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    node.addEventListener('keydown', handleKeyDown);
    return () => node.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div ref={containerRef} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} style={{ position: 'relative', width: '100%' }}>
      {/* @ts-ignore Apparently there is a mismatch in React/types and TSC causing false negatives ¯\_(ツ)_/¯ */}
      <Geocoder
        accessToken={import.meta.env.VITE_REACT_APP_MAP_BOX_PUBLIC_TOKEN}
        interceptSearch={(searchValue) => (parseLatLon(searchValue) ? '' : searchValue)}
        onChange={(nextValue) => setInputText(nextValue)}
        onRetrieve={(res) => {
          if (!res.properties.context.region || !('region_code' in res.properties.context.region)) {
            // TODO: add alert once we have one, but this should never happen given how we restrict result types below
            return;
          }

          onSelect({
            coordinates: { latitude: res.geometry.coordinates[1], longitude: res.geometry.coordinates[0] },
            fullAddress: res.properties.full_address,
            regionCode: res.properties.context.region.region_code as string,
          });
        }}
        ref={ref}
        value={value || ''}
        placeholder={t('siteLookup.addressSearch.placeholder')}
        popoverOptions={{ offset: 4 }}
        theme={{
          cssText: css(theme),
          variables: {
            colorPrimary: text.primary,
            colorSecondary: text.secondary,
            colorText: text.primary,
            border: '0',
            borderRadius: borderRadius.lg,
          },
        }}
        options={{ country: 'US,CA', types: 'postcode,district,place,locality,neighborhood,street,address,secondary_address' }}
      />

      {showRecentSearches && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 0.5,
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 8px 24px rgb(20 24 40 / 12%)',
            overflow: 'hidden',
            zIndex: zIndex.modal,
          }}
        >
          <Typography color="text.secondary" fontSize={12} fontWeight={700} sx={{ paddingX: 2, paddingTop: 1, paddingBottom: 0.5 }}>
            {t('siteLookup.recentSearches.title')}
          </Typography>

          <MenuList dense sx={{ maxHeight: 320, overflowY: 'auto', paddingTop: 0 }}>
            {recentSearches.map((search) => (
              <MenuItem
                key={search.fullAddress}
                // Prevent the input from blurring before the click registers, which would hide the list first.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelectRecent(search)}
                sx={{ paddingY: 1 }}
              >
                <ListItemText primary={search.fullAddress} slotProps={{ primary: { noWrap: true, fontSize: 14 } }} />
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      )}
    </div>
  );
});

export default AddressSearch;
